import React from 'react';
import * as Yup from 'yup';
import { Button, Dialog, Group, Spinner, Tabs } from '@economic/taco';
import { t, Trans } from '@lingui/macro';
import { useCurrentAgreement } from '@economic/react-app';
import DimensionLineCombobox from '~/app/shared/dimensions/components/DimensionLineCombobox';
import useDimensionsAccessLevel from '~/app/shared/dimensions/hooks/useDimensionsAccessLevel';
import useDimensions, { useOneDimension } from '~/app/shared/dimensions/hooks/useDimensions';
import { DimensionsAccess } from '~/app/shared/dimensions/types';
import { useAccounts } from '~/app/shared/hooks/masterdata/useAccounts';
import { Column, Row } from '../../shared/components/Grid/Grid';
import { Condition, Form, Field } from '../../shared/components/Form';
import AccessDropdown from '../../shared/components/ui/AccessDropdown';
import AccountComboBox from '../../shared/components/ui/AccountCombobox';
import AccountTypeDropdown from '../../shared/components/ui/AccountTypeDropdown';
import DebitCreditDropdown from '../../shared/components/ui/DebitCreditDropdown';
import VatCodeDropdown from '../../shared/components/ui/VatCodeDropdown';
import { FormProps } from '../types';
import internalApiClient from '../../shared/util/api/internalApiClient';
import KeyFigureCodeDropdown from '../../shared/components/ui/KeyFigureCodeDropdown';
import UnitDropdown from '../../shared/components/ui/UnitDropdown';
import CurrencyDropdown from '../../shared/components/ui/CurrencyDropdown';
import { Account } from '../../../types/internal-api';
import useCachedQuery from '../../shared/hooks/useCachedQuery';
import { AccountTypeNumber, Entity } from '../../../types';
import ConfirmationDialog from '../../shared/components/modals/ConfirmationDialog';
import useQuery from '../../shared/hooks/useQuery';
import StandardAccountCombobox from '../../shared/components/ui/StandardAccountCombobox';
import MirrorCodeDropDown from '../../shared/components/ui/MirrorCodeDropDown';

const isFieldDisabled = (name, values) => {
    switch (name) {
        case 'vatCode':
        case 'accounts.contraAccount':
        case 'accounts.realisationAccount':
        case 'debitCredit':
        case 'access':
        case 'isBlockedForDirectEntries':
        case 'currency':
        case 'standardAccountId':
            return values.type !== 1 && values.type !== 2;

        case 'accounts.openingAccount':
            return values.type !== 2;

        case 'accounts.totalFromAccount':
            return values.type !== 3;

        case 'keyFigureCode':
            return values.type !== 1 && values.type !== 2 && values.type !== 3 && values.type !== 6;

        case 'totalIntervals':
            return values.type !== 6;

        default:
            return false;
    }
};

const initialValues: Partial<Account> = {
    type: 1,
    number: undefined,
    name: undefined,
    vatCode: undefined,
    accounts: {
        contraAccount: undefined,
        totalFromAccount: undefined,
        openingAccount: undefined,
        realisationAccount: undefined,
    },
    keyFigureCode: undefined,
    debitCredit: false,
    access: true,
    isBlockedForDirectEntries: false,
    totalIntervals: undefined,
    dimension: {
        department: undefined,
        isDepartmentMandatory: false,
        isUnitMandatory: false,
        unit1: undefined,
        unit2: undefined,
        mirrorCodeId: undefined,
    },
    dimensions: [],
    currency: undefined,
};

const validationSchema = values =>
    Yup.object({
        type: Yup.number().required(),
        number: Yup.number().required(),
        name: Yup.string().required(),
        vatCode: Yup.string().nullable(),
        displayNumber: Yup.string()
            .matches(/^(?!0+$)\d+$/, `${t`Invalid account number`}`)
            .required(),
        accounts: Yup.object({
            contraAccount: Yup.number().test('contraAccount', '', validateAccount()).nullable(),
            totalFromAccount: Yup.number()
                .lessThan(values.number, `${t`Total from need to be lower than account number`}`)
                .test('totalFromAccount', '', validateAccount())
                .nullable(),
            openingAccount: Yup.number().test('openingAccount', '', validateAccount()).nullable(),
            realisationAccount: Yup.number().test('realisationAccount', '', validateAccount()).nullable(),
        }).nullable(),
        keyFigureCode: Yup.number().nullable(),
        reference1: Yup.string()
            .nullable()
            .max(100, t`Note can not be longer than 100 characters`),
        debitCredit: Yup.boolean().required(),
        access: Yup.boolean().required(),
        isBlockedForDirectEntries: Yup.boolean().required(),
        dimension: Yup.object({
            department: Yup.number()
                .test('department', '', function (department) {
                    const { dimensions: departments, hasMultiDimensions } = this.options.context as any;

                    // validate department (single dimension) if agreement doesn't have multiple dimensions
                    if (!hasMultiDimensions && department && !departments.some(dep => dep.number === department && dep.access)) {
                        return this.createError({
                            path: this.path,
                            message: t`Invalid department number`,
                        });
                    }
                    return true;
                })
                .nullable(),
            isDepartmentMandatory: Yup.boolean(),
            isUnitMandatory: Yup.boolean(),
            unit1: Yup.number().nullable(),
            unit2: Yup.number().nullable(),
        }).nullable(),
        currency: Yup.string(),
        standardAccountId: Yup.number()
            .test('standardAccountId', '', function (standardAccountId) {
                const standardAccounts = (this.options.context! as any).standardAccount.data as any[];
                if (standardAccountId && !standardAccounts.some(standartAccount => standartAccount.id === standardAccountId)) {
                    return this.createError({
                        path: this.path,
                        message: t`Invalid standart account number`,
                    });
                }
                return true;
            })
            .nullable()
            .min(1, t`Invalid account number`)
            .max(32767, t`Invalid account number`),
    });

export function validateAccount(): (this: Yup.TestContext, value: any) => boolean | Yup.ValidationError {
    return function (accountNumber) {
        const accounts = (this.options.context! as any).accounts as any[];

        /*
         * Check if chosen account is barred
         */
        if (accountNumber && !accounts.some(account => account.number === accountNumber && account.access)) {
            return this.createError({
                path: this.path,
                message: t`Invalid account number`,
            });
        }

        /**
         * Check if chosen _openingAccount_ is an invalid type (Heading, HeadingBreak, TotalFrom, or TotalIntervals).
         */
        if (
            this.path === 'accounts.openingAccount' &&
            accountNumber &&
            accounts.some(
                account =>
                    account.number === accountNumber &&
                    [
                        AccountTypeNumber.Heading,
                        AccountTypeNumber.HeadingBreak,
                        AccountTypeNumber.TotalFrom,
                        AccountTypeNumber.TotalIntervals,
                    ].includes(account.type.number)
            )
        ) {
            return this.createError({
                path: this.path,
                message: t`Invalid account number`,
            });
        }

        return true;
    };
}

const getUpdateValue = (key, type, allValues) => (isFieldDisabled(key, { type }) ? undefined : allValues[key]);

const updater = [
    {
        field: 'type',
        updates: (type, _, allValues: any = {}) => {
            return {
                'accounts.contraAccount': getUpdateValue('accounts.contraAccount', type, allValues),
                'accounts.openingAccount': getUpdateValue('accounts.openingAccount', type, allValues),
                'accounts.totalFromAccount': getUpdateValue('accounts.totalFromAccount', type, allValues),
                'accounts.realisationAccount': getUpdateValue('accounts.realisationAccount', type, allValues),
                currency: getUpdateValue('currency', type, allValues),
                vatCode: getUpdateValue('vatCode', type, allValues),
            };
        },
    },
    {
        field: 'displayNumber',
        updates: displayNumber => {
            return {
                number: parseInt(displayNumber),
            };
        },
    },
];

enum CreateAccountTab {
    General = 'tab-general',
    Dimension = 'tab-dimension',
    Mapping = 'tab-standardaccount-mapping',
}

type CreateOrEditAccountProps = Omit<FormProps<Account>, 'onSubmitted'> & {
    visible?: boolean;
    onSubmitted: (createNew: boolean | undefined) => void;
};

export default function CreateOrEditAccount({
    initialData = initialValues,
    notifications,
    onSubmitted,
    onSubmitError,
    onCancel,
}: CreateOrEditAccountProps) {
    const agreement = useCurrentAgreement();
    const hasDimension = useDimensionsAccessLevel(DimensionsAccess.HasDimension);
    const hasMultiDimensions = useDimensionsAccessLevel(DimensionsAccess.HasMultiDimensions);
    const hasMirrorcodes = agreement.modules.mirrorcodes;
    const hasAccrualModule = agreement.modules.accruals || agreement.modules.fixedAssetsAndAccruals;
    const [createCounter, setCreateCounter] = React.useState(1);
    const [createNew, setCreateNew] = React.useState(false);
    const [showNumberChangeDialog, setShowNumberChangeDialog] = React.useState(false);
    const isCreatingNewOrCopying = initialData.number === undefined;
    const changeAccountNumberQuery = useCachedQuery<boolean>(`/settings/entity-numbers/${Entity.Account}`, false);
    const { data: accounts, loading: accountsLoading } = useAccounts({ showAllAccounts: true });
    const departments = useOneDimension(1);
    const standardAccount = useCachedQuery('/standard-account');
    const dimesionSettingsQuery = useQuery<{ showUnit1: boolean; showUnit2: boolean }>(
        'dimension/settings',
        {},
        {
            skip: !hasDimension,
        }
    );
    const dimensionsDefinitionsQuery = useDimensions();

    if (
        hasDimension &&
        dimesionSettingsQuery.loading &&
        accountsLoading &&
        departments.loading &&
        standardAccount.loading &&
        !dimensionsDefinitionsQuery.__initialized
    ) {
        return <Spinner />;
    }

    const handleSubmit = async values => {
        if (values.type !== 1 && values.type !== 2) {
            values.standardAccountId = null;
        }

        if (hasMultiDimensions) {
            // ignore department (single dimension) if multiple dimensions are enabled
            values.dimension.department = undefined;
        } else {
            values.dimensions = [
                {
                    dimensionId: 1,
                    dimensionKey: values.dimension.department,
                    isMandatory: values.dimension.isDepartmentMandatory,
                },
            ];
        }

        values.displayNumber = values.displayNumber !== `${values.number}` ? values.displayNumber : undefined;

        try {
            if (isCreatingNewOrCopying) {
                const response = await internalApiClient.post<Account>(`/accounts`, values);
                return Promise.resolve(response);
            }
            const response = await internalApiClient.put<Account>(`/accounts/${initialData.number}`, values);
            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    if (isCreatingNewOrCopying && hasMultiDimensions && dimensionsDefinitionsQuery.data)
        initialData.dimensions = dimensionsDefinitionsQuery.data.map(dimension => ({
            dimensionId: dimension.id,
            dimensionKey: undefined,
            isMandatory: false,
        }));

    return (
        <Form
            initialData={initialData}
            messages={notifications}
            onSubmit={handleSubmit}
            onSubmitted={_ => {
                onSubmitted?.(createNew);
                if (createNew) {
                    setCreateCounter(createCounter + 1);
                }
            }}
            onSubmitError={onSubmitError}
            updates={updater}
            context={{ accounts, departments, standardAccount, hasMultiDimensions }}
            validationSchema={values => validationSchema(values)}>
            {({ values, handleSubmit }) => {
                const defaultFields = (
                    <Row divided>
                        <Column xs="3">
                            <Field.Dropdown
                                label={t`Type`}
                                name="type"
                                as={AccountTypeDropdown}
                                required
                                includeAllTypesButSumAlpha
                            />
                            <Field.Text
                                label={t`No.`}
                                name="displayNumber"
                                defaultValue={isCreatingNewOrCopying ? '' : `${values.number}`}
                                required
                            />
                            <Field.Text label={t`Name`} name="name" required />
                        </Column>

                        <Column xs="3">
                            <Field.Dropdown
                                label={t`VAT`}
                                name="vatCode"
                                disabled={isFieldDisabled('vatCode', values)}
                                as={VatCodeDropdown}
                                emptyValue={null}
                                initialValue={values.vatCode} // needs to be explicitly set, or emptyValue overwrites it in the Form's internal state
                            />
                            <Field.Combobox
                                label={t`Contra account`}
                                name="accounts.contraAccount"
                                disabled={isFieldDisabled('accounts.contraAccount', values)}
                                as={AccountComboBox}
                            />

                            <Field.Combobox
                                label={t`Total from account`}
                                name="accounts.totalFromAccount"
                                allowHeadingClick
                                disabled={isFieldDisabled('accounts.totalFromAccount', values)}
                                as={AccountComboBox}
                            />
                        </Column>

                        <Column xs="3">
                            <Field.Combobox
                                label={<Trans context="Primokonto">Opening account</Trans>}
                                name="accounts.openingAccount"
                                disabled={isFieldDisabled('accounts.openingAccount', values)}
                                as={AccountComboBox}
                            />
                            {hasAccrualModule && (
                                <Field.Combobox
                                    label={t`Realisation account`}
                                    name="accounts.realisationAccount"
                                    disabled={isFieldDisabled('accounts.realisationAccount', values)}
                                    as={AccountComboBox}
                                />
                            )}
                            <Field.Dropdown
                                label={t`Key figure code`}
                                name="keyFigureCode"
                                disabled={isFieldDisabled('keyFigureCode', values)}
                                as={KeyFigureCodeDropdown}
                                emptyValue={null}
                            />
                            <Field.Text label={t`Note`} name="reference1" />
                        </Column>

                        <Column xs="3">
                            <Row>
                                <Column xs="6">
                                    <Field.Dropdown
                                        label={t`Debit/Credit`}
                                        name="debitCredit"
                                        disabled={isFieldDisabled('debitCredit', values)}
                                        as={DebitCreditDropdown}
                                    />
                                </Column>
                                <Column xs="6">
                                    <Field.Dropdown
                                        label={t`Access`}
                                        name="access"
                                        disabled={isFieldDisabled('access', values)}
                                        as={AccessDropdown}
                                    />
                                </Column>
                            </Row>
                            <Row>
                                <Column>
                                    <Field.Checkbox
                                        text={t`Block direct entries`}
                                        name="isBlockedForDirectEntries"
                                        disabled={isFieldDisabled('isBlockedForDirectEntries', values)}
                                    />

                                    <Condition for="type" when={type => type === 1 || type === 2}>
                                        <Row>
                                            <Column xs="6">
                                                <Field.Dropdown label={t`Currency`} name="currency" as={CurrencyDropdown} />
                                            </Column>
                                        </Row>
                                    </Condition>
                                    <Condition for="type" when={type => type === 6}>
                                        <Field.Text
                                            label={t`Total intervals`}
                                            name="totalIntervals"
                                            disabled={isFieldDisabled('totalIntervals', values)}
                                        />
                                    </Condition>
                                </Column>
                            </Row>
                        </Column>
                    </Row>
                );

                return (
                    <>
                        <h1>{t`Account`}</h1>

                        <Tabs defaultId={CreateAccountTab.General}>
                            <Tabs.List>
                                <Tabs.Trigger id={CreateAccountTab.General}>{t`General`}</Tabs.Trigger>
                                {hasDimension && <Tabs.Trigger id={CreateAccountTab.Dimension}>{t`Dimension`}</Tabs.Trigger>}
                                <Condition for="type" when={type => type === 1 || type === 2}>
                                    <Tabs.Trigger id={CreateAccountTab.Mapping}>{t`Mapping`}</Tabs.Trigger>
                                </Condition>
                            </Tabs.List>
                            <Tabs.Content id={CreateAccountTab.General}>{defaultFields}</Tabs.Content>
                            {hasDimension && (
                                <Tabs.Content id={CreateAccountTab.Dimension}>
                                    <Row divided>
                                        {!hasMultiDimensions && (
                                            <Column xs="4">
                                                <Group>
                                                    <DimensionLineCombobox
                                                        name="dimension.department"
                                                        dimensionId={1}
                                                        label={dimensionsDefinitionsQuery.getDimensionName(1)}
                                                    />
                                                    <Field.Checkbox text={t`Mandatory`} name="dimension.isDepartmentMandatory" />
                                                </Group>
                                            </Column>
                                        )}
                                        {hasMultiDimensions && dimensionsDefinitionsQuery.data && (
                                            <Column xs="4">
                                                <div className="flex flex-col gap-2 mb-2">
                                                    {initialData?.dimensions?.map((dimension, index) => {
                                                        const dimensionDefinition = dimensionsDefinitionsQuery.data.find(
                                                            x => x.id === dimension?.dimensionId
                                                        );

                                                        if (typeof dimensionDefinition === 'undefined') return null;
                                                        return (
                                                            <Group
                                                                key={dimensionDefinition.id}
                                                                className="w-full flex flex-row justify-between mb-2">
                                                                <Field.Hidden
                                                                    name={`dimensions[${index}].dimensionId`}
                                                                    initialValue={dimensionDefinition.id}
                                                                />
                                                                <DimensionLineCombobox
                                                                    name={`dimensions[${index}].dimensionKey`}
                                                                    dimensionId={dimensionDefinition.id}
                                                                    fieldClassName="flex flex-1 flex-col -mb-4 w-1"
                                                                    label={dimensionDefinition.name}
                                                                    initialValue={dimension?.dimensionKey}
                                                                />
                                                                <Field.Checkbox
                                                                    text={t`Mandatory`}
                                                                    name={`dimensions[${index}].isMandatory`}
                                                                />
                                                            </Group>
                                                        );
                                                    })}
                                                </div>
                                            </Column>
                                        )}
                                        <Column xs="3">
                                            {(dimesionSettingsQuery.data.showUnit1 || dimesionSettingsQuery.data.showUnit2) && (
                                                <Field.Checkbox text={t`Mandatory unit`} name="dimension.isUnitMandatory" />
                                            )}
                                            {dimesionSettingsQuery.data.showUnit1 && (
                                                <Field.Dropdown
                                                    label={t`Unit`}
                                                    name="dimension.unit1"
                                                    as={UnitDropdown}
                                                    emptyValue={null}
                                                />
                                            )}
                                            {dimesionSettingsQuery.data.showUnit2 && (
                                                <Field.Dropdown
                                                    label={t`Unit 2`}
                                                    name="dimension.unit2"
                                                    as={UnitDropdown}
                                                    emptyValue={null}
                                                />
                                            )}
                                        </Column>
                                        {hasMirrorcodes && (
                                            <Column xs="3">
                                                <Field.Dropdown
                                                    label={t`Mirror code`}
                                                    name="dimension.mirrorCodeId"
                                                    as={MirrorCodeDropDown}
                                                    initialValue={values.dimension.mirrorCodeId}
                                                    emptyValue={null}
                                                />
                                            </Column>
                                        )}
                                    </Row>
                                </Tabs.Content>
                            )}
                            <Condition for="type" when={type => type === 1 || type === 2}>
                                <Tabs.Content id={CreateAccountTab.Mapping}>
                                    <Row>
                                        <Column>
                                            <b>
                                                {t`Map your account to the Danish Business Authority's standard account
                                                            plan.`}
                                            </b>
                                            <br />
                                            {t`Here you can map your account to an account in the Danish Business
                                                        Authority's standard account plan. You can then draw a report with your
                                                        accounting data, presented in the Danish Business Authority's standard
                                                        chart of accounts.`}
                                        </Column>
                                    </Row>
                                    <br />
                                    <br />
                                    <Row divided>
                                        <Column xs="4">
                                            <Field.Combobox
                                                label={t`Choose account from standard chart of accounts`}
                                                name="standardAccountId"
                                                as={StandardAccountCombobox}
                                            />
                                        </Column>
                                    </Row>
                                </Tabs.Content>
                            </Condition>
                        </Tabs>

                        <div className="form__buttons">
                            {isCreatingNewOrCopying && (
                                <Button type="submit" appearance="primary" onClick={() => setCreateNew(true)}>
                                    <Trans>Save & new</Trans>
                                </Button>
                            )}
                            <Button
                                type={
                                    isCreatingNewOrCopying ||
                                    (initialData.number && initialData.number !== values.number && changeAccountNumberQuery?.data)
                                        ? 'button'
                                        : 'submit'
                                }
                                appearance={isCreatingNewOrCopying ? 'default' : 'primary'}
                                onClick={e => {
                                    setCreateNew(false);
                                    if (
                                        !(
                                            initialData.number &&
                                            initialData.number !== values.number &&
                                            changeAccountNumberQuery?.data
                                        ) ||
                                        isCreatingNewOrCopying
                                    ) {
                                        handleSubmit(e);
                                    } else if (
                                        initialData.number &&
                                        initialData.number !== values.number &&
                                        changeAccountNumberQuery?.data
                                    ) {
                                        setShowNumberChangeDialog(true);
                                    }
                                }}>
                                <Trans>Save</Trans>
                            </Button>
                            <Button onClick={onCancel} appearance="default">
                                <Trans>Cancel</Trans>
                            </Button>
                        </div>
                        {showNumberChangeDialog && (
                            <Dialog size="xs" defaultOpen>
                                <Dialog.Content aria-label={t`Change number`}>
                                    <ConfirmationDialog
                                        label={t`New number?`}
                                        message={t`Do you want to change the account number?`}
                                        onCancel={() => setShowNumberChangeDialog(false)}
                                        onConfirm={handleSubmit}
                                    />
                                </Dialog.Content>
                            </Dialog>
                        )}
                    </>
                );
            }}
        </Form>
    );
}
